/**
 * "This user still owes us the onboarding."
 *
 * The hand-off used to travel only in the router's navigation state, which does
 * not survive a reload or a guard redirect. When it was lost the user landed on
 * /home with a half-finished profile — no name, no belt, no academy — and had
 * no way back, because the onboarding has no entry point of its own.
 *
 * Keeping the flag in sessionStorage makes the pending onboarding a fact about
 * the session rather than a property of one navigation, so the guards can put
 * the user back where they belong.
 */

export const ONBOARDING_PATH = '/onboarding';

const KEY = 'pendingOnboarding';

export interface PendingOnboarding {
  username: string;
  isInstructor: boolean;
}

const isPending = (value: unknown): value is PendingOnboarding =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PendingOnboarding).username === 'string';

export const readPendingOnboarding = (): PendingOnboarding | null => {
  if (typeof sessionStorage === 'undefined') return null;

  const raw = sessionStorage.getItem(KEY);
  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isPending(parsed)) {
      // Malformed value: drop it rather than trapping the user in a redirect
      // loop to a screen the onboarding can't render.
      sessionStorage.removeItem(KEY);
      return null;
    }
    return { username: parsed.username, isInstructor: !!parsed.isInstructor };
  } catch {
    sessionStorage.removeItem(KEY);
    return null;
  }
};

export const markOnboardingPending = (pending: PendingOnboarding): void => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(KEY, JSON.stringify(pending));
};

export const clearPendingOnboarding = (): void => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(KEY);
};
